import { createContext, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchSession } from "./api";
import type { PortalSession } from "./types";

const SessionContext = createContext<PortalSession | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["session"],
    queryFn: fetchSession,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  if (isLoading) {
    return <div className="empty">Signing you in…</div>;
  }
  if (isError || !data) {
    return <div className="empty">Could not establish a session.</div>;
  }
  return <SessionContext.Provider value={data}>{children}</SessionContext.Provider>;
}

export function useSession(): PortalSession {
  const session = useContext(SessionContext);
  if (!session) {
    throw new Error("useSession outside SessionProvider");
  }
  return session;
}

// Server-returned capabilities are the ONLY gating source (never a client
// role table). This is affordance-hiding, not enforcement — the BFF enforces.
export function useCan(capability: string): boolean {
  return useSession().capabilities.includes(capability);
}
